import { clsx } from '@alexis/helpers/clsx';
import React, { useEffect, useState } from 'react';

import Portal from '@components/Portal';

import { useToast } from '@context/ToastContext';

import InfoIcon from '@icons/info.svg';
import WarningIcon from '@icons/warning.svg';

import styles from './Toast.module.scss';

const NAVBAR_HEIGHT = 72;
const OFFSET = 24;

const Toast = ({ isHomePage }: { isHomePage: boolean }) => {
  const { toastConfig, resetToast } = useToast();
  const { message, showIcon, action, variant, position, duration } = toastConfig;
  const [top, setTop] = useState(NAVBAR_HEIGHT + OFFSET);

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;
    if (message) {
      timerId = setTimeout(() => {
        resetToast();
      }, duration);
    }
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [message, duration, resetToast]);

  useEffect(() => {
    // No offset calculation on home page due to sticky navbar
    if (isHomePage) {
      return;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY >= NAVBAR_HEIGHT) {
        setTop(OFFSET);
      } else {
        const newPosition = OFFSET + (NAVBAR_HEIGHT - scrollY);
        setTop(newPosition);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  if (message) {
    return (
      <Portal>
        <div
          className={clsx(
            styles.container,
            styles[`pos-${position}`],
            styles[`variant-${variant}`],
            message.length > 80 && styles.longMessage,
          )}
          style={{ top: `${top}px` }}
        >
          <div className={styles.contentWrapper}>
            {showIcon && (
              <div className={styles.iconWrapper}>
                {variant === 'error' ? (
                  <WarningIcon role='img' aria-label='Warning icon' className={styles.icon} />
                ) : (
                  <InfoIcon role='img' aria-label='Info icon' className={styles.icon} />
                )}
              </div>
            )}
            <div className={styles.message}>{message}</div>
          </div>
          {action && (
            <div className={clsx(styles.actionWrapper, message.length > 80 && styles.longMessage)}>
              <div className={styles.action} onClick={action.onClick}>
                {action.label}
              </div>
            </div>
          )}
        </div>
      </Portal>
    );
  }

  return null;
};

export default Toast;
